import DataTable from "@components/DataTable";
import {
  Button,
  Card,
  Form,
  OverlayTrigger,
  Popover,
  Tab,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import { SetStateAction, useState, useEffect, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";

import {
  faClockRotateLeft,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { useNavigate } from "react-router-dom";
import { useDeleteUserMutation, useUpdateUserMutation } from "@generated";
import useListUsers from "../../api/useListUsers";
import useGetAccount from "../../api/useGetAccount";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
const strftime = require("strftime");

import SlideoutMenu from "@components/SlideOutMenuV2";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";

export interface UserRow {
  id: number;
  type: string;
  active?: boolean;
  name: string;
  phone?: string;
  email: string;
  title?: string;
  teamId?: string[];
  teamNames: string;
  roleIds: number[];
  roleNames: string;
  userType?: string;
  createdAt: string;
}

export interface TeamData {
  id: number;
  type: string;
  teamName: string;
}

interface Option {
  label: string;
  value: string;
}

const UsersTable = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [userToSearch, setUserToSearch] = useState("");
  const [filter, setFilter] = useState<{
    name?: string;
    title?: Option[];
    teamName?: Option[];
    roleName?: Option[];
    userType?: Option[];
    startDate?: string;
    endDate?: string;
  }>({});
  const [filteredUsers, setFilteredUsers] = useState<UserRow[]>([]);
  const [searchApplied, setSearchApplied] = useState(false);
  const [filterDraft, setFilterDraft] = useState<typeof filter>({});
  const [userCounts, setUserCounts] = useState<{
    active: number | null;
    archived: number | null;
  }>({ active: null, archived: null });

  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");

  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [deleteUser, {}] = useDeleteUserMutation();
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  type UserType = "view_only" | "licensed" | "sales";

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data: account } = useGetAccount("scopestack-demo");
  const { data, team, isLoading, refetch, pageMeta } = useListUsers({
    page: { number: pageNumber, size: pageSize },
    filter: { active: activeTab === "active" },
  });

  const { data: allUserData } = useListUsers({
    page: { number: 1, size: 1000 },
    filter: { active: activeTab === "active" },
  });

  useEffect(() => {
    if (!allUserData) return;

    const count = allUserData.length;

    setUserCounts((prev) => {
      if (activeTab === "active" && prev.active !== count) {
        return { ...prev, active: count };
      }
      if (activeTab === "archived" && prev.archived !== count) {
        return { ...prev, archived: count };
      }
      return prev;
    });
  }, [allUserData, activeTab]);

  useEffect(() => {
    setPageNumber(1);
  }, [filter, userToSearch, activeTab]);

  const roleNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    allUsers.forEach((u) => {
      u.roleNames.split(",").forEach((name, idx) => {
        const id = u.roleIds[idx];
        if (name && id) map.set(name.trim().toLowerCase(), String(id));
      });
    });
    return map;
  }, [allUsers]);

  const teamNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    team?.forEach((t) => {
      const name = t.attributes?.name?.trim().toLowerCase();
      if (name && t.id) map.set(name, String(t.id));
    });
    return map;
  }, [team]);

  const apiFilter = useMemo(() => {
    const filterObject: Record<string, string> = {};

    if (filter.name) filterObject.name = filter.name;
    if (filter.title?.length)
      filterObject.title = filter.title.map((o) => o.value.trim()).join(",");
    if (filter.teamName?.length) {
      const teamIds = filter.teamName
        .map((o) => teamNameToIdMap.get(o.value.trim().toLowerCase()))
        .filter((id): id is string => !!id);
      if (teamIds.length) filterObject.team = teamIds.join(",");
    }
    if (filter.roleName?.length) {
      const roleIds = filter.roleName
        .map((o) => roleNameToIdMap.get(o.value.trim().toLowerCase()))
        .filter((id): id is string => !!id);
      if (roleIds.length) filterObject.role = roleIds.join(",");
    }
    if (filter.startDate) filterObject["created-at.after"] = filter.startDate;
    if (filter.endDate) filterObject["created-at.before"] = filter.endDate;

    return filterObject;
  }, [filter, teamNameToIdMap, roleNameToIdMap]);

  let users: UserRow[] = [];
  let teams: TeamData[] = [];
  let customError: string | null = null;
  if (team) {
    try {
      teams = team.map((team) => {
        if (!team.id) {
          throw new Error("Missing user.id");
        }

        return {
          id: team.id,
          type: team.type,
          teamName: team.attributes?.name || "",
        };
      });
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  const teamMap = useMemo(
    () =>
      team?.reduce((acc, t) => {
        if (t.id && t.attributes?.name) {
          acc[String(t.id)] = t.attributes.name;
        }
        return acc;
      }, {} as Record<string, string>) || {},
    [team]
  );

  const getTeamNamesByIds = (ids: string[]): string => {
    const matchedNames = ids.map((id) => teamMap?.[id] || "").filter(Boolean);
    return matchedNames.join(", ");
  };

  if (data) {
    try {
      users = data.map((user) => {
        if (!user.id) {
          throw new Error("Missing user.id");
        }
        const teamIds =
          user.relationships?.teams?.data?.map((team) => String(team.id)) || [];
        return {
          id: user.id,
          type: user.type,
          active: user.attributes?.active || true,
          name: user.attributes?.name || "",
          title: user.attributes?.title || "",
          email: user.attributes?.email || "",
          phone: user.attributes?.phone || "",
          teamId: teamIds,
          teamNames: getTeamNamesByIds(teamIds),
          roleIds: user.attributes?.roles?.map((role) => role.id) || [],
          roleNames:
            user.attributes?.roles?.map((role) => role.name).join(", ") || "",
          userType: user.attributes?.["user-type"] || "",
          createdAt: user.attributes?.["created-at"] || "",
        };
      });
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  useEffect(() => {
    if (!allUserData || allUserData.length === 0) return;
    if (!teamMap || Object.keys(teamMap).length === 0) return;

    const mapped = allUserData.map((user) => {
      const teamIds =
        user.relationships?.teams?.data?.map((t) => String(t.id)) || [];
      return {
        id: user?.id || 0,
        type: user.type,
        active: user.attributes?.active,
        name: user.attributes?.name || "",
        title: user.attributes?.title || "",
        email: user.attributes?.email || "",
        phone: user.attributes?.phone || "",
        teamId: teamIds,
        teamNames: getTeamNamesByIds(teamIds),
        roleIds: user.attributes?.roles?.map((r) => r.id) || [],
        roleNames: user.attributes?.roles?.map((r) => r.name).join(", ") || "",
        userType: user.attributes?.["user-type"] || "",
        createdAt: user.attributes?.["created-at"] || "",
      };
    });
    setAllUsers((prev) => {
      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(mapped);
      return prevStr === nextStr ? prev : mapped;
    });
  }, [allUserData, teamMap]);

  const uniqueTitles = Array.from(
    new Set(users.map((u) => u.title).filter(Boolean))
  );

  const titleOptions: Option[] = uniqueTitles
    .filter((title): title is string => !!title)
    .map((title) => ({
      label: title,
      value: title,
    }));

  const teamOptions: Option[] = Array.from(
    new Set(
      allUsers
        .flatMap(
          (u) => u.teamNames?.split(",").map((name) => name.trim()) || []
        )
        .filter(Boolean)
    )
  ).map((team) => ({ label: team, value: team }));

  const roleOptions: Option[] = Array.from(
    new Set(
      allUsers
        .flatMap(
          (u) => u.roleNames?.split(",").map((name) => name.trim()) || []
        )
        .filter(Boolean)
    )
  ).map((role) => ({ label: role, value: role }));

  const userTypeOptions: Option[] = Array.from(
    new Set(allUsers.map((u) => u.userType).filter(Boolean))
  ).map((type) => ({
    label: type as string,
    value: type as string,
  }));

  const searchUser = (searchValue: string, activeFilter = filter) => {
    const trimmed = searchValue.toLowerCase();
    let results = allUsers;

    if (trimmed) {
      results = results.filter((u) => u.name.toLowerCase().includes(trimmed));
    }

    results = results.filter((u) => {
      const created = new Date(u.createdAt);

      const selectedTitles =
        activeFilter.title?.map((opt) => opt.value.toLowerCase()) || [];
      const userTitle = (u.title || "").toLowerCase();
      const matchesTitle =
        selectedTitles.length === 0 || selectedTitles.includes(userTitle);

      const selectedTeamNames =
        activeFilter.teamName?.map((opt) => opt.value.toLowerCase()) || [];
      const userTeams = (u.teamNames || "")
        .split(",")
        .map((t) => t.trim().toLowerCase());
      const matchesTeam =
        selectedTeamNames.length === 0 ||
        selectedTeamNames.every((name) => userTeams.includes(name));

      const selectedRoleNames =
        activeFilter.roleName?.map((opt) => opt.value.toLowerCase()) || [];
      const userRoles = (u.roleNames || "")
        .split(",")
        .map((r) => r.trim().toLowerCase());
      const matchesRole =
        selectedRoleNames.length === 0 ||
        selectedRoleNames.every((name) => userRoles.includes(name));

      const selectedUserTypes =
        activeFilter.userType?.map((opt) => opt.value.toLowerCase()) || [];
      const userType = (u.userType || "").toLowerCase();
      const matchesUserType =
        selectedUserTypes.length === 0 || selectedUserTypes.includes(userType);

      const matchesStart = activeFilter.startDate
        ? created >= new Date(activeFilter.startDate)
        : true;

      const matchesEnd = activeFilter.endDate
        ? created <= new Date(activeFilter.endDate)
        : true;
      return (
        matchesTitle &&
        matchesTeam &&
        matchesRole &&
        matchesUserType &&
        matchesStart &&
        matchesEnd
      );
    });
    setFilteredUsers(results);
    setSearchApplied(true);
  };

  const handleUserSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = userToSearch.trim();

    if (trimmed === "") {
      searchUser("", filter);
      setPageNumber(1);
      return;
    }
    searchUser(trimmed);
    setPageNumber(1);
  };

  const handleSlideoutSave = () => {
    setFilter(filterDraft);
    setSearchApplied(true);
    searchUser(userToSearch, filterDraft);
    setIsSlideoutOpen(false);
  };

  const handleDelete = (id?: string) => {
    if (id) {
      setDeleteQueue([id]);
    } else {
      setDeleteQueue([...selectedIds]);
    }
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    if (deleteQueue.length === 0) return;

    const id = deleteQueue[0];
    deleteUser({ slug: accountSlug, id: parseInt(id) })
      .unwrap()
      .then(() => {
        refetch();
      })
      .catch((error) => {
        console.error(error);
      });

    setDeleteQueue(deleteQueue.slice(1));
  };

  const handleConfirm = () => {
    confirmDelete();
    if (deleteQueue.length <= 1) {
      setShowConfirmationModal(false);
      setSelectedIds([]);
    }
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
    setDeleteQueue([]);
  };

  const getConfirmationMessage = () => {
    const targetId = Number(deleteQueue[0]);

    const userToDelete = users.find((user) => user.id == targetId) || {
      name: "",
      title: "",
      email: "",
      phone: "",
      createdAt: "",
      teamId: "",
      teamNames: "",
      roleIds: 0,
      roleNames: "",
    };

    const { name } = userToDelete;

    return (
      <>
        <p
          style={{
            color: "var(--navy-1, #1C2655)",
            fontSize: "14px",
            fontWeight: "700",
          }}
        >
          Are you sure you want to delete {name}?
        </p>
        <p
          style={{
            color: "var(--navy-1, #1C2655)",
            fontSize: "14px",
          }}
        >
          Deleting a user is final and cannot be undone.
        </p>
      </>
    );
  };

  const paginatedFilteredUsers = useMemo(() => {
    const start = (pageNumber - 1) * pageSize;
    const end = start + pageSize;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, pageNumber, pageSize]);

  const handleConvertUserType = async (userId: number, newType: UserType) => {
    const user = allUsers.find((u) => u.id === userId);
    if (!user) {
      console.error("User not found.");
      return;
    }
    try {
      await updateUser({
        slug: accountSlug,
        id: userId,
        body: {
          data: {
            type: "users",
            id: userId,
            attributes: {
              name: user.name,
              email: user.email,
              "user-type": newType || undefined,
            },
          },
        },
      }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to update type:", error);
    }
  };

  const handleUnarchiveUsers = (userIds: string[]) => {
    // Call your backend update logic here to mark them active
    userIds.forEach(async (id) => {
      try {
        await updateUser({
          slug: accountSlug,
          id: parseInt(id),
          body: {
            data: {
              type: "users",
              id: parseInt(id),
              //@ts-ignore
              attributes: {
                active: true,
              },
            },
          },
        }).unwrap();
      } catch (error) {
        console.error("Failed to unarchive user:", error);
      }
    });

    // Optionally refresh data or show toast
    refetch();
    setSelectedIds([]);
  };

  const columns: ColumnDef<UserRow>[] = [
    {
      header: "Name",
      cell(props) {
        return (
          <div
            className="userName"
            onClick={() => {
              localStorage.setItem("lastPage", window.location.href);
              const user = props.row.original;

              if (user && user.roleNames && user.roleIds) {
                const userSnapshot = {
                  id: user.id,
                  type: "users",
                  attributes: {
                    active: user.active,
                    name: user.name,
                    email: user.email,
                    phone: user.phone,
                    title: user.title,
                    "user-type": user.userType,
                    roles: user.roleIds.map((id, index) => ({
                      id,
                      name: user.roleNames.split(",")[index]?.trim() || "",
                    })),
                  },
                  relationships: {
                    teams: {
                      data:
                        user.teamId?.map((id) => ({ id, type: "teams" })) || [],
                    },
                  },
                };
                if (activeTab === "archived") {
                  navigate(`edit/${user.id}`, {
                    state: { userSnapshot },
                  });
                } else {
                  navigate(`edit/${user.id}`);
                }
              }
            }}
            style={{ display: "flex" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                margin: "0px 8px 0px 8px",
              }}
              className="userNameText"
            >
              <span
                style={{
                  width: "fit-content",
                  color: "#1c2655",
                  fontFamily: "Open Sans",
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "normal",
                  textDecorationLine: "underline",
                  textDecorationStyle: "solid",
                  textDecorationSkipInk: "auto",
                  textDecorationThickness: "auto",
                  textUnderlineOffset: "auto",
                  textUnderlinePosition: "from-font",
                  marginRight: "4px",
                }}
              >
                {props.row.original.name}
              </span>
              <div style={{ flexShrink: "0", width: "16px", height: "16px" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13.8067 4.69354C14.0667 4.43354 14.0667 4.0002 13.8067 3.75354L12.2467 2.19354C12 1.93354 11.5667 1.93354 11.3067 2.19354L10.08 3.41354L12.58 5.91354M2 11.5002V14.0002H4.5L11.8733 6.6202L9.37333 4.1202L2 11.5002Z"
                    fill="#1C2655"
                  />
                </svg>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      header: "Title",
      cell(props) {
        return (
          <div className="userTitle">{props.row.original?.title || ""}</div>
        );
      },
    },
    {
      header: "Email",
      cell(props) {
        return (
          <div className="userEmail">{props.row.original.email || ""}</div>
        );
      },
    },
    {
      header: "Phone",
      cell(props) {
        return (
          <div className="userPhone">{props.row.original?.phone || ""}</div>
        );
      },
    },
    {
      header: "Added On",
      cell(props) {
        const createdAtStr = props.row.original?.createdAt || "";
        const formatStr = `${
          account?.attributes?.["short-date-format"] || "%m/%d/%Y"
        }`;

        const formattedDate = createdAtStr
          ? strftime(formatStr, new Date(createdAtStr))
          : "";
        return <div className="userAdded">{formattedDate}</div>;
      },
    },
    {
      header: "Team",
      cell(props) {
        return (
          <div className="userTeam">{props.row.original.teamNames || ""}</div>
        );
      },
    },
    {
      header: "Role",
      cell(props) {
        const role = props.row.original.roleNames || "";
        const type = props.row.original.userType || "";
        return (
          <div className="userRole">
            <span>{role}</span>
            {type && (
              <span
                style={{
                  display: "flex",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
              >
                {type === "view_only"
                  ? "View-Only"
                  : type === "licensed"
                  ? "Licensed"
                  : type === "sales"
                  ? "Sales"
                  : type}
              </span>
            )}
          </div>
        );
      },
    },
    ...(activeTab !== "archived"
      ? [
          {
            header: " ",
            cell(props) {
              const currentType = props.row.original.userType;
              return (
                <div
                  style={{
                    color: "var(--navy-1, #1c2655)",
                    fontFamily: "Open Sans",
                    fontSize: "16px",
                    fontWeight: "400",
                  }}
                  className="userTeam"
                >
                  <OverlayTrigger
                    trigger="click"
                    placement="left"
                    rootClose
                    overlay={
                      <Popover id={`popover-${props.row.original.id}`}>
                        <Popover.Body>
                          {currentType !== "licensed" && (
                            <div
                              style={{ cursor: "pointer", color: "#1c2655" }}
                              onClick={() =>
                                handleConvertUserType(
                                  props.row.original.id,
                                  "licensed"
                                )
                              }
                            >
                              Convert to Licensed User
                            </div>
                          )}
                          {currentType !== "sales" && (
                            <div
                              style={{ cursor: "pointer", color: "#1c2655" }}
                              onClick={() =>
                                handleConvertUserType(
                                  props.row.original.id,
                                  "sales"
                                )
                              }
                            >
                              Convert to Sales User
                            </div>
                          )}
                          {currentType !== "view_only" && (
                            <div
                              style={{ cursor: "pointer", color: "#1c2655" }}
                              onClick={() =>
                                handleConvertUserType(
                                  props.row.original.id,
                                  "view_only"
                                )
                              }
                            >
                              Convert to View-Only User
                            </div>
                          )}
                          <div
                            style={{ cursor: "pointer", color: "#1c2655" }}
                            onClick={() =>
                              handleDelete(String(props.row.original.id))
                            }
                          >
                            Delete
                          </div>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="17"
                      viewBox="0 0 16 17"
                      fill="none"
                      style={{ cursor: "pointer" }}
                    >
                      <path
                        d="M4.88686 8.5C4.88686 6.81563 6.2522 5.5 8.00022 5.5C9.71905 5.5 11.1136 6.81563 11.1136 8.5C11.1136 10.1562 9.71905 11.5 8.00022 11.5C6.2522 11.5 4.88686 10.1562 4.88686 8.5ZM8.00022 7C7.1408 7 6.44354 7.67188 6.44354 8.5C6.44354 9.32812 7.1408 10 8.00022 10C8.85964 10 9.5569 9.32812 9.5569 8.5C9.5569 7.67188 8.85964 7 8.00022 7ZM9.2034 0.500009C9.92337 0.500009 10.5493 0.974688 10.7179 1.64813L10.9709 2.66281C11.2465 2.7925 11.5092 2.93969 11.7589 3.1025L12.8 2.80719C13.4908 2.61125 14.2302 2.89594 14.5902 3.49594L15.7933 5.50313C16.1501 6.10313 16.0366 6.8625 15.5177 7.34062L14.7296 8.03438C14.7393 8.20625 14.7458 8.35313 14.7458 8.5C14.7458 8.64688 14.7393 8.79375 14.7296 8.9375L15.5177 9.65938C16.0366 10.1375 16.1501 10.8969 15.7933 11.4969L14.5902 13.5031C14.2302 14.1031 13.4908 14.3875 12.8 14.1938L11.7589 13.8969C11.5092 14.0594 11.2465 14.2062 10.9709 14.3375L10.7179 15.3531C10.5493 16.025 9.92337 16.5 9.2034 16.5H6.79704C6.07707 16.5 5.45116 16.025 5.28252 15.3531L5.02956 14.3375C4.75389 14.2062 4.49121 14.0594 4.24149 13.8969L3.17127 14.1938C2.51066 14.3875 1.77156 14.1031 1.4119 13.5031L0.208717 11.4969C-0.15094 10.8969 -0.0371732 10.1375 0.48373 9.65938L1.26985 8.9375C1.2598 8.79375 1.25461 8.64688 1.25461 8.47188C1.25461 8.35313 1.2598 8.20625 1.26985 8.03438L0.48373 7.34062C-0.0372056 6.8625 -0.15094 6.10313 0.208717 5.50313L1.4119 3.49594C1.77156 2.89594 2.51066 2.61125 3.17127 2.80719L4.24149 3.1025C4.49121 2.93969 4.75389 2.7925 5.02956 2.66281L5.28252 1.64813C5.45116 0.974375 6.07707 0.5 6.79704 0.5L9.2034 0.500009ZM6.36246 3.7375L5.99924 3.88438C5.58737 4.05 5.20468 4.26562 4.85767 4.52187L4.54309 4.75313L2.76005 4.24688L1.55654 6.25313L2.90242 7.4875L2.8528 7.8625C2.82556 8.07188 2.81129 8.28438 2.81129 8.5C2.81129 8.71562 2.82556 8.92812 2.8528 9.1375L2.90242 9.5125L1.55687 10.7469L2.76005 12.7531L4.54309 12.2469L4.85767 12.4781C5.20468 12.7344 5.58737 12.95 5.99924 13.1156L6.36246 13.2625L6.79704 15H9.2034L9.63797 13.2625L10.0012 13.1156C10.4131 12.95 10.7958 12.7344 11.1428 12.4781L11.4573 12.2469L13.241 12.7531L14.4442 10.7469L13.0983 9.5125L13.147 9.1375C13.1762 8.92812 13.1891 8.71562 13.1891 8.5C13.1891 8.28438 13.1762 8.07188 13.147 7.8625L13.0983 7.4875L14.4442 6.25313L13.241 4.24688L11.4573 4.75313L11.1428 4.52187C10.7958 4.26562 10.4131 4.05 10.0012 3.88438L9.63797 3.7375L9.2034 2H6.79704L6.36246 3.7375Z"
                        fill="#1C2655"
                      />
                    </svg>
                  </OverlayTrigger>
                </div>
              );
            },
          },
        ]
      : []),
  ];
  if (isLoading) {
    return <ScopeStackSpinner />;
  }

  return (
    <div className="usersTable">
      <Card
        style={{
          padding: "10px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: " var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "unset",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div className="accordianTitle" style={{ margin: "0" }}>
              Users
            </div>
            <div className="userActionBtns">
              <Button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "0px",
                  fontSize: "16px",
                  fontWeight: "700",
                  padding: "8px 16px",
                  height: "100%",
                  background: "#418172",
                  color: "white",
                }}
                className="userRegButton"
                onClick={() => navigate(`create`)}
              >
                Add a User
              </Button>
            </div>
          </div>
          <hr></hr>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {selectedIds.length > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="action-tooltip">
                    {activeTab === "active" ? "Delete" : "Un-archive"}
                  </Tooltip>
                }
              >
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={() => {
                    if (activeTab === "active") {
                      handleDelete();
                    } else {
                      // This is where you'd call your unarchive logic
                      handleUnarchiveUsers(selectedIds);
                    }
                  }}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  <FontAwesomeIcon
                    style={{ color: "black" }}
                    icon={
                      activeTab === "active" ? faTrashCan : faClockRotateLeft
                    }
                  />
                </Button>
              </OverlayTrigger>
            )}
            {/* {selectedIds.length > 0 && (
              <Button
                style={{
                  marginRight: "10px",
                  alignSelf: "flex-end",
                  background: "unset",
                  color: "var(--teal-1, #418172)",
                  fontWeight: "700",
                  width: "11em",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                }}
                onClick={() => handleDelete()}
              >
                {selectedIds.length} Selected{" "}
                <span
                  style={{
                    borderLeft: "1px solid #D7D7D7",
                    height: "24px",
                    margin: "0 8px",
                  }}
                ></span>
                <FontAwesomeIcon
                  style={{ color: "black" }}
                  icon={faClockRotateLeft}
                />
              </Button>
            )} */}
            <div style={{ width: "-webkit-fill-available" }}>
              <FormFieldLabel label="Search Users" className="searchBarTitle" />
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setSearchApplied(true);
                    handleUserSearch(e);
                  }
                }}
                onSubmit={handleUserSearch}
              >
                <div
                  className="searchFieldInput"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    backgroundColor: "#fff",
                  }}
                >
                  <SearchField
                    placeholder={"Search By Name"}
                    onChange={(e: {
                      target: { value: SetStateAction<string> };
                    }) => {
                      setUserToSearch(e.target.value);
                    }}
                    value={userToSearch}
                    onClick={() => {
                      setUserToSearch("");
                      setFilter({});
                      setSearchApplied(false);
                      setPageNumber(1);
                    }}
                    fullWidth
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    style={{ cursor: "pointer", marginLeft: "0.25rem" }}
                    fill="none"
                    onClick={() => {
                      setFilterDraft(filter);
                      setIsSlideoutOpen(true);
                    }}
                  >
                    <g clipPath="url(#clip0_14546_22082)">
                      <path
                        d="M10 18H14V16H10V18ZM3 6V8H21V6H3ZM6 13H18V11H6V13Z"
                        fill="#1C2655"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_14546_22082">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </Form>
            </div>
          </div>
        </Card.Header>

        <SlideoutMenu
          isOpen={isSlideoutOpen}
          onClose={() => setIsSlideoutOpen(false)}
          onSave={() => {
            handleSlideoutSave();
          }}
          onClearAll={() => {
            setFilter({});
            setFilterDraft({});
            setFilteredUsers([]);
            setSearchApplied(false);
            setIsSlideoutOpen(false);
          }}
          title="Filter By"
          filterCount={Object.keys(filter).length}
          actionBtnText="Apply"
        >
          <div style={{ padding: "1rem" }}>
            {/* Title Filter */}
            <MultiSelectAutocomplete
              label="Title"
              id="title-filter"
              options={titleOptions}
              value={filterDraft.title || []}
              onChange={(selected) =>
                setFilterDraft((prev) => ({
                  ...prev,
                  title: selected as Option[],
                }))
              }
            />
            {/* Team Filter */}
            <MultiSelectAutocomplete
              label="Team"
              id="team-filter"
              options={teamOptions}
              value={filterDraft.teamName || []}
              onChange={(selected) =>
                setFilterDraft((prev) => ({
                  ...prev,
                  teamName: selected as Option[],
                }))
              }
            />
            {/* Role Filter */}
            <MultiSelectAutocomplete
              label="Role"
              id="role-filter"
              options={roleOptions}
              value={filterDraft.roleName || []}
              onChange={(selected) =>
                setFilterDraft((prev) => ({
                  ...prev,
                  roleName: selected as Option[],
                }))
              }
            />
            {/* Type Filter */}
            <MultiSelectAutocomplete
              label="Type"
              id="type-filter"
              options={userTypeOptions}
              value={filterDraft.userType || []}
              onChange={(selected) =>
                setFilterDraft((prev) => ({
                  ...prev,
                  userType: selected as Option[],
                }))
              }
            />
            <div
              className="SearchByDate"
              style={{
                marginTop: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              {/* Created After */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <FormFieldLabel
                  className="filterSearchCreatedAfter"
                  label="Date Added Start"
                />
                <Form.Control
                  type="date"
                  value={filterDraft.startDate || ""}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />
              </div>
              <span style={{ whiteSpace: "nowrap" }}>To</span>

              {/* Created Before */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <FormFieldLabel
                  className="filterSearchCreatedBefore"
                  label="Date Added End"
                />
                <Form.Control
                  type="date"
                  value={filterDraft.endDate || ""}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </SlideoutMenu>

        <div className="usersTableTabsWrapper">
          <Tabs
            id="userStatusTabs"
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k as "active" | "archived");
              setSelectedIds([]);
            }}
            className="activeUserTabs"
          >
            <Tab
              eventKey="active"
              title={
                activeTab === "active"
                  ? `Active Users (${userCounts.active ?? "..."})`
                  : "Active Users"
              }
            >
              <hr className="userTabsDivider" />
              <DataTable
                data={searchApplied ? paginatedFilteredUsers : users}
                columns={columns}
                selectable
                bordered
                striped
                hover
                totalPages={
                  searchApplied
                    ? Math.ceil(filteredUsers.length / pageSize)
                    : pageMeta["page-count"]
                }
                totalRows={
                  searchApplied
                    ? filteredUsers.length
                    : pageMeta["record-count"]
                }
                currentPage={pageNumber}
                setCurrentPage={setPageNumber}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                paginationEnabled
                pageSizeEnabled
              />
            </Tab>
            <Tab
              eventKey="archived"
              title={
                activeTab === "archived"
                  ? `Archived Users (${userCounts.archived ?? "..."})`
                  : "Archived Users"
              }
            >
              <hr className="userTabsDivider" />
              <DataTable
                data={searchApplied ? paginatedFilteredUsers : users}
                columns={columns}
                selectable
                bordered
                striped
                hover
                totalPages={
                  searchApplied
                    ? Math.ceil(filteredUsers.length / pageSize)
                    : pageMeta["page-count"]
                }
                totalRows={
                  searchApplied
                    ? filteredUsers.length
                    : pageMeta["record-count"]
                }
                currentPage={pageNumber}
                setCurrentPage={setPageNumber}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                paginationEnabled
                pageSizeEnabled
              />
            </Tab>
          </Tabs>
        </div>
      </Card>
      <ConfirmationModal
        title="Confirm Delete"
        message={getConfirmationMessage()}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        show={showConfirmationModal}
        onHide={handleCancel}
      />
    </div>
  );
};
export default UsersTable;
